/**
 * #978 — where the note panel goes.
 *
 * Anchored: to the RIGHT of the card, top-aligned with it (the reference
 * mockup). Contained: centred in the viewport, height-bounded, used whenever
 * the anchored panel would cross the right edge or there is no card left to
 * anchor to. Never flipped to the card's left — that would sit on the
 * neighbouring card's price and cancel controls — and never clipped: the
 * panel is `position: fixed`, so anything pushed off-screen is unreachable.
 */

export const NOTE_PANEL_WIDTH = 320;
/** Gap between the card's right edge and the panel. */
export const NOTE_PANEL_GAP = 8;
/** Smallest gap kept from every viewport edge. */
export const VIEWPORT_MARGIN = 8;
/** Below this much room, an anchored panel slides up instead of shrinking. */
export const MIN_PANEL_HEIGHT = 220;

export type NotePanelPlacement =
  | { mode: 'anchored'; top: number; left: number; maxHeight: number }
  | { mode: 'contained'; maxHeight: number };

export function notePanelPlacement(
  card: Pick<DOMRect, 'top' | 'right'> | null,
  viewport: { width: number; height: number },
): NotePanelPlacement {
  const containedHeight = Math.max(0, viewport.height - 2 * VIEWPORT_MARGIN);
  if (!card) return { mode: 'contained', maxHeight: containedHeight };
  const left = card.right + NOTE_PANEL_GAP;
  if (left + NOTE_PANEL_WIDTH > viewport.width - VIEWPORT_MARGIN) {
    return { mode: 'contained', maxHeight: containedHeight };
  }
  let top = Math.max(VIEWPORT_MARGIN, card.top);
  if (viewport.height - top - VIEWPORT_MARGIN < MIN_PANEL_HEIGHT) {
    top = Math.max(VIEWPORT_MARGIN, viewport.height - VIEWPORT_MARGIN - MIN_PANEL_HEIGHT);
  }
  return { mode: 'anchored', top, left, maxHeight: viewport.height - top - VIEWPORT_MARGIN };
}

export function samePlacement(a: NotePanelPlacement, b: NotePanelPlacement): boolean {
  if (a.mode !== b.mode || a.maxHeight !== b.maxHeight) return false;
  return a.mode === 'contained' || (b.mode === 'anchored' && a.top === b.top && a.left === b.left);
}

/** Exact, local-time edit stamp, e.g. "Sep 27, 2026, 2:17:04 PM". */
const EDITED_AT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
});

export function formatEditedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : EDITED_AT.format(d);
}
